import { useState, useEffect } from "react";
import { PayoutLetterSettings } from "./PayoutLetterSettings";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Settings, Shield, Clock, DollarSign, Bell, Loader2, RefreshCw, Key, Eye, EyeOff, CheckCircle, XCircle, Play, Phone, Plus, Trash2, GripVertical, Building2, Database, Upload, Download, Save, MessageSquare, Banknote, Mail, Scale, Link2, Hash, Search, Users, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getPortalSettings, updateSetting, getApiCredentials, updateApiCredentials, testApiCredentials, getPrefundingQuestions, createPrefundingQuestion, updatePrefundingQuestion, deletePrefundingQuestion, reorderPrefundingQuestions, checkCollections2025Data, exportCollections2025Data, importCollections2025Data, getSalesSyncStatus, triggerSalesSync, updateSyncSchedule, listChaserSmsTemplates, createChaserSmsTemplate, updateChaserSmsTemplate, deleteChaserSmsTemplate, resetChaserSmsTemplates, updateChaserSmsGroupLabels, listChaserEmailTemplates, createChaserEmailTemplate, updateChaserEmailTemplate, deleteChaserEmailTemplate, resetChaserEmailTemplates, updateChaserEmailGroupLabels } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ThirdPartyLendersSettings from "@/pages/settings/ThirdPartyLendersSettings";
import LegalDiarySettings from "@/pages/settings/LegalDiarySettings";
import AccountStatusSettings from "@/pages/settings/AccountStatusSettings";
import ComplaintsSettings from "@/pages/settings/ComplaintsSettings";
import XeroSettings from "@/pages/XeroSettings";
import SlackCommsSettings from "@/pages/settings/SlackCommsSettings";
import AircallAiSettings from "@/pages/settings/AircallAiSettings";
import NoticeTemplatesSettings from "@/pages/settings/NoticeTemplatesSettings";
import PartnerApiKeysCard from "@/pages/admin/PartnerApiKeys";
import LoanDiskWebhooksCard from "@/pages/admin/LoanDiskWebhooksCard";
import MailboxTrafficCard from "@/pages/admin/MailboxTrafficCard";

// Setting row component - defined outside to avoid re-renders
function DevAccessCard() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pw.trim().length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.put("/auth/dev-bypass-password", { new_password: pw.trim() });
      toast.success("Dev access password updated — existing bypass sessions were signed out");
      setPw(""); setPw2("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="dev-access-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-600" />
          Dev Access Password
        </CardTitle>
        <CardDescription>
          The shared password for the emergency "Team access" login on the sign-in page (used when Clerk is unavailable). Changing it signs out anyone currently using dev access.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">New password</Label>
            <div className="relative">
              <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)}
                className="h-9 w-52 pr-9" placeholder="Min 6 characters" data-testid="dev-access-new-password" />
              <button type="button" onClick={() => setShow(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Confirm</Label>
            <Input type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)}
              className="h-9 w-52" placeholder="Repeat password" data-testid="dev-access-confirm-password" />
          </div>
          <Button onClick={save} disabled={saving || !pw.trim()} data-testid="dev-access-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Update password
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Setting row component - defined outside to avoid re-renders
function SettingRow({ label, description, settingKey, type = "number", suffix = "", value, onSave, saving }) {
  const [localValue, setLocalValue] = useState(value ?? "");
  const [hasChanged, setHasChanged] = useState(false);
  
  // Update local value when prop changes (only if user hasn't edited)
  const currentValue = hasChanged ? localValue : (value ?? "");

  const handleChange = (newValue) => {
    setLocalValue(newValue);
    setHasChanged(true);
  };

  const handleSave = () => {
    if (localValue !== value) {
      onSave(settingKey, localValue);
      setHasChanged(false);
    }
  };

  if (type === "boolean") {
    return (
      <div className="flex items-center justify-between py-3 border-b last:border-0">
        <div>
          <p className="font-medium text-sm">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Switch
          checked={value ?? false}
          onCheckedChange={(checked) => onSave(settingKey, checked)}
          disabled={saving === settingKey}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex-1">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <Input
          type={type}
          value={currentValue}
          onChange={(e) => handleChange(type === "number" ? parseFloat(e.target.value) : e.target.value)}
          className="w-24 text-right"
          onBlur={handleSave}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
        />
        {suffix && <span className="text-sm text-muted-foreground w-16">{suffix}</span>}
        {saving === settingKey && <Loader2 className="w-4 h-4 animate-spin" />}
      </div>
    </div>
  );
}

// API Credential Card Component
function ApiCredentialCard({ serviceId, service, onUpdate, onTest }) {
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [publicKeyDialog, setPublicKeyDialog] = useState(null); // {pem, message}

  const handleFieldChange = (fieldKey, value) => {
    setCredentials(prev => ({ ...prev, [fieldKey]: value }));
  };

  const toggleShowPassword = (fieldKey) => {
    setShowPasswords(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(serviceId, credentials);
      setCredentials({});
      toast.success(`${service.name} credentials updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update credentials");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTest(serviceId);
      setTestResult(result);
      if (result.status === "success") {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      setTestResult({ status: "error", message: error.response?.data?.detail || "Test failed" });
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  // Optional custom actions (only used by GoCardless Outbound Payments today).
  // Each action key is handled inline so we don't add a generic action-registry
  // just for one caller — keeps things simple + explicit.
  const handleAction = async (actionKey) => {
    setActionBusy(actionKey);
    try {
      const { gcOutboundGenerateKeypair, gcOutboundShowPublicKey, gcOutboundVerifyLive } =
        await import("@/lib/api");
      if (actionKey === "generate_keypair") {
        const confirmed = window.confirm(
          "Generate a NEW ES512 keypair? This overwrites the previous private key on this portal. " +
          "You'll then need to paste the new public key into GoCardless dashboard to keep live " +
          "signing working."
        );
        if (!confirmed) return;
        const res = await gcOutboundGenerateKeypair();
        setPublicKeyDialog({ pem: res.public_key_pem, message: res.message });
        toast.success("New ES512 keypair generated");
      } else if (actionKey === "show_public_key") {
        const res = await gcOutboundShowPublicKey();
        setPublicKeyDialog({ pem: res.public_key_pem, message: "Paste this into GoCardless dashboard." });
      } else if (actionKey === "verify_live") {
        const res = await gcOutboundVerifyLive();
        setTestResult(res);
        if (res.status === "success") toast.success(res.message);
        else toast.error(res.message);
      } else if (actionKey === "capay_verify") {
        const { default: apiClient } = await import("@/lib/api");
        const { data } = await apiClient.post("/api-credentials/capay_payments/verify");
        setTestResult(data);
        toast.success(data.message);
      } else if (actionKey === "capay_register_webhook") {
        const { default: apiClient } = await import("@/lib/api");
        const { data } = await apiClient.post("/api-credentials/capay_payments/register-webhook");
        toast.success(data.message);
      } else if (actionKey === "onedrive_aus_diagnose") {
        const { default: apiClient } = await import("@/lib/api");
        const { data } = await apiClient.post("/legal/cases/files/diagnose?region=AUS");
        const msg = data.ok
          ? `AU OneDrive OK — share "${data.share?.name}" readable and a test upload succeeded.`
          : `AU OneDrive failed at ${data.stage}: ${data.error}${data.missing_scopes?.length ? ` (missing Graph roles: ${data.missing_scopes.join(", ")})` : ""}`;
        setTestResult({ status: data.ok ? "success" : "error", message: msg });
        (data.ok ? toast.success : toast.error)(msg);
      } else if (actionKey === "creditorwatch_verify" || actionKey === "abr_verify") {
        const { default: apiClient } = await import("@/lib/api");
        const { data } = await apiClient.post(`/api-credentials/${actionKey === "abr_verify" ? "abr_register" : "creditorwatch"}/verify`);
        setTestResult(data);
        toast.success(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Action failed: ${actionKey}`);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <Card className="mb-4" data-testid={`api-cred-card-${serviceId}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {service.all_configured ? (
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              ) : (
                <XCircle className="w-4 h-4 text-amber-500" />
              )}
              {service.name}
            </CardTitle>
            <CardDescription className="text-xs">{service.description}</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={testing || !service.all_configured}
            data-testid={`api-cred-test-${serviceId}`}
          >
            {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
            Test
          </Button>
        </div>
        {testResult && (
          <div className={`mt-2 text-xs p-2 rounded ${testResult.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
            {testResult.message}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {service.fields.map((field) => (
            <div key={field.key} className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-2">
                {field.label}
                {field.configured ? (
                  <span className="text-emerald-600 text-[10px]">✓ Configured{field.type === "select" && field.current_value ? ` (${field.current_value.toUpperCase()})` : ""}</span>
                ) : (
                  <span className="text-amber-500 text-[10px]">Not set</span>
                )}
              </Label>
              {field.type === "select" ? (
                <Select
                  value={credentials[field.key] ?? field.current_value ?? field.default ?? ""}
                  onValueChange={(v) => handleFieldChange(field.key, v)}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid={`api-cred-select-${serviceId}-${field.key}`}>
                    <SelectValue placeholder={`Choose ${field.label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(field.options || []).map(opt => (
                      <SelectItem key={opt} value={opt} data-testid={`api-cred-select-${serviceId}-${field.key}-opt-${opt}`}>
                        {opt.toUpperCase()}{opt === "live" ? " — REAL MONEY" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : field.type === "textarea" ? (
                field.key === "live_signing_key_pem" ? (
                  // Generated-only field: never a paste target. "Generate ES512
                  // Keypair" writes the private key server-side; pasting the
                  // PUBLIC key here (a common slip) used to clobber it.
                  <div className="rounded-md border bg-muted/40 p-3 space-y-1" data-testid={`api-cred-pem-status-${serviceId}`}>
                    <p className="text-xs font-medium flex items-center gap-1.5">
                      {field.configured
                        ? <><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Private key stored on the server</>
                        : <><AlertCircle className="w-3.5 h-3.5 text-amber-500" /> No private key yet</>}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      This is set only via <strong>Generate ES512 Keypair</strong> below — you never paste it.
                      The button stores the private key here and gives you the <strong>public</strong> key to add in GoCardless.
                    </p>
                  </div>
                ) : (
                  <Textarea
                    rows={5}
                    placeholder={field.configured ? field.masked_value : `Paste ${field.label.toLowerCase()} here`}
                    value={credentials[field.key] || ""}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="text-xs font-mono"
                    data-testid={`api-cred-textarea-${serviceId}-${field.key}`}
                  />
                )
              ) : (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={field.type === "password" && !showPasswords[field.key] ? "password" : "text"}
                      placeholder={field.configured ? field.masked_value : `Enter ${field.label.toLowerCase()}`}
                      value={credentials[field.key] || ""}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      className="text-sm pr-8"
                      data-testid={`api-cred-input-${serviceId}-${field.key}`}
                    />
                    {field.type === "password" && (
                      <button
                        type="button"
                        onClick={() => toggleShowPassword(field.key)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {Object.keys(credentials).some(k => credentials[k]) && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="mt-4 w-full"
            size="sm"
            data-testid={`api-cred-save-${serviceId}`}
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Save {service.name} Credentials
          </Button>
        )}

        {Array.isArray(service.actions) && service.actions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
            {service.actions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant || "outline"}
                size="sm"
                onClick={() => handleAction(action.key)}
                disabled={actionBusy === action.key}
                data-testid={`api-cred-action-${serviceId}-${action.key}`}
              >
                {actionBusy === action.key && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {publicKeyDialog && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setPublicKeyDialog(null)}
            data-testid={`api-cred-pubkey-dialog-${serviceId}`}
          >
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-2">GoCardless ES512 Public Key</h3>
              <p className="text-sm text-muted-foreground mb-3">{publicKeyDialog.message}</p>
              <Textarea
                readOnly
                rows={9}
                value={publicKeyDialog.pem}
                className="text-xs font-mono"
                data-testid={`api-cred-pubkey-pem-${serviceId}`}
              />
              <div className="flex gap-2 mt-3 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(publicKeyDialog.pem);
                    toast.success("Public key copied to clipboard");
                  }}
                  data-testid={`api-cred-pubkey-copy-${serviceId}`}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPublicKeyDialog(null)}
                  data-testid={`api-cred-pubkey-close-${serviceId}`}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Data Management Section Component
function DataManagementSection() {
  const [dataStatus, setDataStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const checkData = async () => {
    try {
      setLoading(true);
      const result = await checkCollections2025Data();
      setDataStatus(result);
    } catch (error) {
      toast.error("Failed to check data status");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkData();
  }, []);

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await exportCollections2025Data();
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collections_2025_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${result.counts?.collections_detailed || 0} records`);
    } catch (error) {
      toast.error("Failed to export data");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate file structure
      if (!data.collections_detailed && !data.collections_daily) {
        throw new Error("Invalid file format - missing collections data");
      }
      
      const result = await importCollections2025Data(data);
      
      toast.success(`Import complete! Imported ${Object.values(result.results).reduce((sum, r) => sum + (r.inserted || 0), 0)} records`);
      checkData(); // Refresh status
    } catch (error) {
      const errorMsg = error.response?.status === 404 
        ? "Import endpoint not found - please redeploy the latest code first"
        : (error.response?.data?.detail || error.message || "Invalid file");
      toast.error("Failed to import data: " + errorMsg);
      console.error(error);
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600" />
          2025 Collections Data
        </CardTitle>
        <CardDescription>
          Manage collections report data for the 2025 analytics. This data is stored in MongoDB and needs to be present for the Collections Analysis page to work.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Check */}
        <div className="border rounded-lg p-4 bg-slate-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Data Status</h4>
            <Button variant="outline" size="sm" onClick={checkData} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
          
          {dataStatus ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {dataStatus.total_documents > 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className="text-sm">
                  {dataStatus.total_documents > 0 
                    ? `${dataStatus.total_documents} total documents found`
                    : "No data found - import required"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Database: {dataStatus.database}
              </div>
              {dataStatus.collections && (
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  {Object.entries(dataStatus.collections).map(([name, info]) => (
                    <div key={name} className="flex justify-between bg-white px-2 py-1 rounded border">
                      <span className="font-mono">{name}</span>
                      <span className={info.count > 0 ? "text-green-600" : "text-red-500"}>
                        {info.count}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Loading...</div>
          )}
        </div>

        {/* Export/Import Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExport} disabled={exporting || !dataStatus?.total_documents}>
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Data
          </Button>
          
          <div className="relative">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={importing}
            />
            <Button variant="default" disabled={importing}>
              {importing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import Data
            </Button>
          </div>
        </div>

        {/* Instructions */}
        {dataStatus?.total_documents === 0 && (
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-amber-700 mb-2">⚠️ Data Missing</h4>
            <p className="text-sm text-muted-foreground">
              The 2025 collections data is not present in this database. To fix this:
            </p>
            <ol className="text-sm text-muted-foreground list-decimal ml-4 mt-2 space-y-1">
              <li>Go to the preview environment's Portal Settings</li>
              <li>Export the 2025 collections data (download JSON file)</li>
              <li>Return here and import the JSON file using the button above</li>
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Monday.com Sync Settings Component
function MondaySyncSection() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncHour, setSyncHour] = useState(6);
  const [syncEnabled, setSyncEnabled] = useState(true);

  const loadSyncStatus = async () => {
    try {
      setLoading(true);
      const status = await getSalesSyncStatus();
      setSyncStatus(status);
      setSyncHour(status.sync_hour || 6);
      setSyncEnabled(status.sync_enabled !== false);
    } catch (error) {
      console.error("Failed to load sync status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSyncStatus();
    // Poll for status if syncing
    const interval = setInterval(() => {
      if (syncing) {
        loadSyncStatus();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [syncing]);

  useEffect(() => {
    if (syncStatus?.status === "completed" && syncing) {
      setSyncing(false);
      toast.success("Sync completed successfully!");
    }
  }, [syncStatus?.status, syncing]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await triggerSalesSync();
      toast.info("Sync started in background...");
    } catch (error) {
      toast.error("Failed to start sync");
      setSyncing(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      await updateSyncSchedule({ sync_hour: syncHour, sync_enabled: syncEnabled });
      toast.success("Sync schedule updated!");
      loadSyncStatus();
    } catch (error) {
      toast.error("Failed to update schedule");
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "Never";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-GB", {
        dateStyle: "medium",
        timeStyle: "short"
      });
    } catch {
      return isoString;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-blue-600" />
          Monday.com Sync Settings
        </CardTitle>
        <CardDescription>
          Configure automatic data synchronization from Monday.com to the Sales Analysis dashboard.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="border rounded-lg p-4 bg-slate-50">
          <h4 className="font-medium mb-3">Sync Status</h4>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Last Sync:</span>
                  <p className="font-medium">{formatDateTime(syncStatus?.last_sync)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Items Synced:</span>
                  <p className="font-medium">{syncStatus?.items_synced?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium flex items-center gap-2">
                    {syncStatus?.status === "running" ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-blue-600">Syncing...</span>
                      </>
                    ) : syncStatus?.status === "completed" ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-green-600">Completed</span>
                      </>
                    ) : syncStatus?.error ? (
                      <>
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-600">Error</span>
                      </>
                    ) : (
                      <span>Unknown</span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Next Auto-Sync:</span>
                  <p className="font-medium">{formatDateTime(syncStatus?.next_scheduled_sync)}</p>
                </div>
              </div>
              
              {syncStatus?.error && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {syncStatus.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manual Sync */}
        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-2">Manual Sync</h4>
          <p className="text-sm text-muted-foreground mb-3">
            Trigger an immediate sync from Monday.com. This will fetch all applications, offers, and funded deals.
          </p>
          <Button 
            onClick={handleSync} 
            disabled={syncing || syncStatus?.status === "running"}
          >
            {syncing || syncStatus?.status === "running" ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
        </div>

        {/* Schedule Settings */}
        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-2">Auto-Sync Schedule</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Configure when the automatic daily sync runs. The sync happens in the background even when no one is using the portal.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-sm">Enable Auto-Sync</label>
                <p className="text-xs text-muted-foreground">Automatically sync data daily</p>
              </div>
              <Switch 
                checked={syncEnabled} 
                onCheckedChange={setSyncEnabled}
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="font-medium text-sm block mb-1">Sync Time (GMT)</label>
                <Select 
                  value={syncHour.toString()} 
                  onValueChange={(v) => setSyncHour(parseInt(v))}
                  disabled={!syncEnabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select hour" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()}>
                        {i.toString().padStart(2, '0')}:00 GMT
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleSaveSchedule}
                disabled={saving}
                className="mt-5"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Schedule
              </Button>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc ml-4 space-y-1 text-blue-700">
            <li>The sync fetches all data from your Monday.com boards (applications, offers, funded deals)</li>
            <li>Data is stored locally in MongoDB for fast access</li>
            <li>The Sales Analysis page loads instantly from the local cache</li>
            <li>Auto-sync ensures data stays fresh without manual intervention</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// Pre-Funding Questions Manager Component
function PreFundingQuestionsManager() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [newQuestion, setNewQuestion] = useState({ text: "", deal_types: ["new", "new_refi", "topup", "topup_refi"], required: true, input_type: "checkbox", options_str: "", placeholder: "" });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editType, setEditType] = useState("checkbox");
  const [editOptions, setEditOptions] = useState("");

  const INPUT_TYPE_OPTIONS = [
    { value: "checkbox", label: "Checkbox (tick to confirm)" },
    { value: "select", label: "Multiple choice" },
    { value: "text", label: "Text answer" },
    { value: "number", label: "Number / amount" },
    { value: "date", label: "Date" },
  ];

  const DEAL_TYPE_OPTIONS = [
    { value: "new", label: "New", color: "emerald" },
    { value: "new_refi", label: "New+Refi", color: "teal" },
    { value: "topup", label: "TopUp", color: "amber" },
    { value: "topup_refi", label: "TopUp+Refi", color: "purple" }
  ];

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      // Load ALL questions for admin management (not filtered by deal type)
      const data = await getPrefundingQuestions(null, false, true);
      setQuestions(data);
    } catch (error) {
      toast.error("Failed to load pre-funding questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.text.trim()) {
      toast.error("Please enter a question");
      return;
    }
    if (!newQuestion.deal_types || newQuestion.deal_types.length === 0) {
      toast.error("Please select at least one deal type");
      return;
    }
    
    try {
      setSaving("add");
      const payload = {
        text: newQuestion.text,
        deal_types: newQuestion.deal_types,
        required: newQuestion.required,
        input_type: newQuestion.input_type,
        options: newQuestion.input_type === "select"
          ? newQuestion.options_str.split(",").map(s => s.trim()).filter(Boolean)
          : null,
        placeholder: newQuestion.placeholder || null,
      };
      if (payload.input_type === "select" && (!payload.options || payload.options.length < 2)) {
        toast.error("Multiple choice questions need at least 2 options (comma-separated)");
        setSaving(null);
        return;
      }
      await createPrefundingQuestion(payload, user?.email || "admin");
      setNewQuestion({ text: "", deal_types: ["new", "new_refi", "topup", "topup_refi"], required: true, input_type: "checkbox", options_str: "", placeholder: "" });
      toast.success("Question added");
      loadQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add question");
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateQuestion = async (questionId) => {
    if (!editText.trim()) return;
    
    try {
      setSaving(questionId);
      const update = { text: editText, input_type: editType };
      if (editType === "select") {
        const opts = editOptions.split(",").map(s => s.trim()).filter(Boolean);
        if (opts.length < 2) {
          toast.error("Multiple choice questions need at least 2 options");
          setSaving(null);
          return;
        }
        update.options = opts;
      }
      await updatePrefundingQuestion(questionId, update, user?.email || "admin");
      setEditingId(null);
      setEditText("");
      toast.success("Question updated");
      loadQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update question");
    } finally {
      setSaving(null);
    }
  };

  const handleToggleRequired = async (question) => {
    try {
      setSaving(question.id);
      // Only send the field being updated (partial update)
      await updatePrefundingQuestion(question.id, { required: !question.required }, user?.email || "admin");
      loadQuestions();
    } catch (error) {
      toast.error("Failed to update question");
    } finally {
      setSaving(null);
    }
  };

  const handleToggleDealType = async (question, dealType) => {
    try {
      setSaving(question.id);
      const currentTypes = question.deal_types || [];
      let newTypes;
      
      if (currentTypes.includes(dealType)) {
        // Remove this type (but don't allow empty)
        newTypes = currentTypes.filter(t => t !== dealType);
        if (newTypes.length === 0) {
          toast.error("Question must apply to at least one deal type");
          setSaving(null);
          return;
        }
      } else {
        // Add this type
        newTypes = [...currentTypes, dealType];
      }
      
      await updatePrefundingQuestion(question.id, { deal_types: newTypes }, user?.email || "admin");
      loadQuestions();
    } catch (error) {
      toast.error("Failed to update question");
    } finally {
      setSaving(null);
    }
  };

  const handleToggleNewDealType = (dealType) => {
    setNewQuestion(prev => {
      const currentTypes = prev.deal_types || [];
      if (currentTypes.includes(dealType)) {
        return { ...prev, deal_types: currentTypes.filter(t => t !== dealType) };
      } else {
        return { ...prev, deal_types: [...currentTypes, dealType] };
      }
    });
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm("Are you sure you want to delete this question?")) return;
    
    try {
      setSaving(questionId);
      await deletePrefundingQuestion(questionId, user?.email || "admin");
      toast.success("Question deleted");
      loadQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete question");
    } finally {
      setSaving(null);
    }
  };

  const startEditing = (question) => {
    setEditingId(question.id);
    setEditText(question.text);
    setEditType(question.input_type || "checkbox");
    setEditOptions((question.options || []).join(", "));
  };

  const handleDropQuestion = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const reordered = [...questions];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setQuestions(reordered);
    setDragIdx(null);
    setOverIdx(null);
    try {
      await reorderPrefundingQuestions(reordered.map((q, i) => ({ id: q.id, order: i })), user?.email || "admin");
      toast.success("Question order saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save the new order");
      loadQuestions();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Count questions by deal type (a question can be in multiple categories)
  const countByType = (type) => questions.filter(q => (q.deal_types || []).includes(type)).length;
  const allTypesCount = questions.filter(q => (q.deal_types || []).length === 4).length;

  return (
    <div className="space-y-6">
      {/* Add New Question */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add New Question
          </CardTitle>
          <CardDescription>
            Create checklist items for the pre-funding call with customers. 
            Select which deal types this question applies to.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Question Text</Label>
              <Input
                value={newQuestion.text}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, text: e.target.value }))}
                placeholder="e.g., Confirmed customer identity (voice verification)"
              />
            </div>
            <div className="space-y-2">
              <Label>Applies To (select all that apply)</Label>
              <div className="flex flex-wrap gap-2">
                {DEAL_TYPE_OPTIONS.map(opt => {
                  const isChecked = (newQuestion.deal_types || []).includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        isChecked 
                          ? opt.color === "emerald" ? "bg-emerald-100 border-emerald-300 text-emerald-800" :
                            opt.color === "teal" ? "bg-teal-100 border-teal-300 text-teal-800" :
                            opt.color === "amber" ? "bg-amber-100 border-amber-300 text-amber-800" :
                            "bg-purple-100 border-purple-300 text-purple-800"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleNewDealType(opt.value)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              {(newQuestion.deal_types || []).length === 4 && (
                <p className="text-xs text-blue-600">✓ Applies to all deal types</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Answer Type</Label>
                <Select value={newQuestion.input_type} onValueChange={(v) => setNewQuestion(prev => ({ ...prev, input_type: v }))}>
                  <SelectTrigger data-testid="new-question-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INPUT_TYPE_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newQuestion.input_type === "select" ? (
                <div>
                  <Label>Options (comma-separated)</Label>
                  <Input
                    value={newQuestion.options_str}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, options_str: e.target.value }))}
                    placeholder="Yes, No, Requires further confirmation"
                    data-testid="new-question-options"
                  />
                </div>
              ) : ["text", "number"].includes(newQuestion.input_type) ? (
                <div>
                  <Label>Placeholder (optional)</Label>
                  <Input
                    value={newQuestion.placeholder}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, placeholder: e.target.value }))}
                    placeholder="e.g. 8 digits"
                  />
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newQuestion.required}
                  onCheckedChange={(checked) => setNewQuestion(prev => ({ ...prev, required: checked }))}
                />
                <Label className={newQuestion.required ? "text-red-600 font-medium" : "text-muted-foreground"}>
                  {newQuestion.required ? "Required" : "Not Required"}
                </Label>
              </div>
            </div>
            <Button onClick={handleAddQuestion} disabled={saving === "add" || !newQuestion.text.trim() || (newQuestion.deal_types || []).length === 0}>
              {saving === "add" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Question
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pre-Funding Call Questions</CardTitle>
          <CardDescription>
            Manage the checklist items shown during pre-funding calls. Click checkboxes to toggle which deal types each question applies to.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {questions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No questions yet. Add your first question above.
            </p>
          ) : (
            <div className="space-y-2">
              {/* Table Header */}
              <div className="flex items-center gap-3 px-3 py-2 bg-slate-100 rounded-lg border text-xs font-medium text-slate-600 uppercase tracking-wide">
                <div className="w-4"></div>
                <div className="flex-1">Question</div>
                <div className="w-48 text-center">Deal Types</div>
                <div className="w-20 text-center">Required</div>
                <div className="w-10 text-center"></div>
              </div>
              
              {questions.map((question, idx) => (
                <div
                  key={question.id}
                  onDragOver={(e) => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); }}
                  onDrop={(e) => { e.preventDefault(); handleDropQuestion(idx); }}
                  className={`flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors ${
                    overIdx === idx && dragIdx !== null && dragIdx !== idx ? "ring-2 ring-blue-400 border-blue-300" : ""
                  } ${dragIdx === idx ? "opacity-50" : ""}`}
                  data-testid={`prefunding-question-row-${idx}`}
                >
                  <div
                    className="flex items-center text-muted-foreground w-4 cursor-grab active:cursor-grabbing"
                    draggable
                    onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                    title="Drag to reorder"
                    data-testid={`prefunding-question-drag-${idx}`}
                  >
                    <GripVertical className="w-4 h-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {editingId === question.id ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="flex-1"
                            autoFocus
                          />
                          <Button size="sm" onClick={() => handleUpdateQuestion(question.id)} disabled={saving === question.id}>
                            {saving === question.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                        </div>
                        <div className="flex gap-2">
                          <Select value={editType} onValueChange={setEditType}>
                            <SelectTrigger className="h-8 text-xs w-48"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {INPUT_TYPE_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {editType === "select" && (
                            <Input
                              value={editOptions}
                              onChange={(e) => setEditOptions(e.target.value)}
                              placeholder="Options, comma-separated"
                              className="h-8 text-xs flex-1"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="cursor-pointer"
                        onClick={() => startEditing(question)}
                      >
                        <p className="text-sm font-medium">
                          {question.text}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <span className="inline-block bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 mr-1">
                            {(INPUT_TYPE_OPTIONS.find(o => o.value === (question.input_type || "checkbox")) || {}).label || question.input_type}
                          </span>
                          {question.input_type === "select" && (question.options || []).length > 0 && (
                            <span className="text-slate-500">[{(question.options || []).join(" / ")}] </span>
                          )}
                          Click to edit
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Deal Types - Multi-select checkboxes */}
                  <div className="w-48 flex flex-wrap gap-1 justify-center">
                    {DEAL_TYPE_OPTIONS.map(opt => {
                      const isChecked = (question.deal_types || []).includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs cursor-pointer transition-colors ${
                            isChecked 
                              ? opt.color === "emerald" ? "bg-emerald-100 text-emerald-700 border border-emerald-200" :
                                opt.color === "teal" ? "bg-teal-100 text-teal-700 border border-teal-200" :
                                opt.color === "amber" ? "bg-amber-100 text-amber-700 border border-amber-200" :
                                "bg-purple-100 text-purple-700 border border-purple-200"
                              : "bg-gray-100 text-gray-400 border border-gray-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleDealType(question, opt.value)}
                            disabled={saving === question.id}
                            className="w-3 h-3 rounded"
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  
                  {/* Required Toggle */}
                  <div className="w-20 flex justify-center">
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={question.required}
                        onCheckedChange={() => handleToggleRequired(question)}
                        disabled={saving === question.id}
                      />
                      <span className={`text-xs ${question.required ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                        {question.required ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                  
                  {/* Delete */}
                  <div className="w-10 flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteQuestion(question.id)}
                      disabled={saving === question.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      {saving === question.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-5 gap-3 text-sm">
            <div>
              <p className="font-medium text-blue-800">Total</p>
              <p className="text-2xl font-bold text-blue-900">{questions.length}</p>
            </div>
            <div>
              <p className="font-medium text-emerald-800">New</p>
              <p className="text-2xl font-bold text-emerald-900">{countByType("new")}</p>
            </div>
            <div>
              <p className="font-medium text-teal-800">New+Refi</p>
              <p className="text-2xl font-bold text-teal-900">{countByType("new_refi")}</p>
            </div>
            <div>
              <p className="font-medium text-amber-800">TopUp</p>
              <p className="text-2xl font-bold text-amber-900">{countByType("topup")}</p>
            </div>
            <div>
              <p className="font-medium text-purple-800">TopUp+Refi</p>
              <p className="text-2xl font-bold text-purple-900">{countByType("topup_refi")}</p>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-3">
            {allTypesCount} question(s) apply to all deal types. Questions can apply to multiple types.
            Questions are stored in the database and shared across all admin users.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Chaser SMS Templates editor ────────────────────────────────────────
// 12 seed templates (4 chasers × 3 tones) with per-row CRUD. Shows the
// list of placeholders at the top so the legal team knows exactly which
// tokens to drop into their body copy.
function ChaserSmsTemplatesEditor() {
  const [templates, setTemplates]       = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [tones, setTones]               = useState([]);
  // Group labels — {"1": "Chaser 1" | "Day 1 SMS", ...}. Editable so ops
  // can rename the whole chaser cluster (heading in the case-page
  // composer dropdown reflects this too).
  const [groupLabels, setGroupLabels]   = useState({});
  const [editingLabel, setEditingLabel] = useState(null);   // group number as string, or null
  const [labelDraft, setLabelDraft]     = useState("");
  const [savingLabel, setSavingLabel]   = useState(false);
  const [loading, setLoading]           = useState(true);
  const [busyId, setBusyId]             = useState(null);
  const [editing, setEditing]           = useState({});
  const [resetting, setResetting]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listChaserSmsTemplates({ onlyActive: false });
      setTemplates(r.templates || []);
      setPlaceholders(r.placeholders || []);
      setTones(r.tones || []);
      setGroupLabels(r.group_labels || {});
    } catch (e) {
      toast.error("Failed to load chaser templates: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startLabelEdit = (grp) => {
    setEditingLabel(String(grp));
    setLabelDraft(groupLabels[String(grp)] || `Chaser ${grp}`);
  };
  const cancelLabelEdit = () => {
    setEditingLabel(null);
    setLabelDraft("");
  };
  const saveLabelEdit = async () => {
    if (!editingLabel) return;
    const clean = (labelDraft || "").trim();
    if (!clean) {
      toast.error("Label cannot be empty");
      return;
    }
    setSavingLabel(true);
    try {
      const r = await updateChaserSmsGroupLabels({ [editingLabel]: clean });
      setGroupLabels(r.labels || {});
      toast.success(`Group ${editingLabel} renamed to "${clean}"`);
      cancelLabelEdit();
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSavingLabel(false);
    }
  };

  const startEdit = (t) => {
    setEditing(prev => ({ ...prev, [t.template_id]: { name: t.name, body: t.body } }));
  };
  const cancelEdit = (id) => {
    setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
  };
  const saveEdit = async (t) => {
    const draft = editing[t.template_id];
    if (!draft) return;
    if (!draft.body || !draft.body.trim()) {
      toast.error("Body cannot be empty");
      return;
    }
    setBusyId(t.template_id);
    try {
      await updateChaserSmsTemplate(t.template_id, {
        name: draft.name,
        body: draft.body,
      });
      toast.success(`Saved ${draft.name || t.name}`);
      cancelEdit(t.template_id);
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const toggleActive = async (t) => {
    setBusyId(t.template_id);
    try {
      await updateChaserSmsTemplate(t.template_id, { active: !t.active });
      await load();
    } catch (e) {
      toast.error("Toggle failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    setBusyId(t.template_id);
    try {
      await deleteChaserSmsTemplate(t.template_id);
      toast.success(`Deleted ${t.name}`);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const resetAll = async () => {
    if (!window.confirm("Reset all templates to the seeded defaults? Any edits will be lost.")) return;
    setResetting(true);
    try {
      const r = await resetChaserSmsTemplates();
      toast.success(`Restored ${r.reseeded} default templates`);
      await load();
    } catch (e) {
      toast.error("Reset failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setResetting(false);
    }
  };
  const addNew = async () => {
    const nextGroup = window.prompt("Chaser group (1-4)?", "1");
    if (!nextGroup) return;
    const nextTone = window.prompt(`Tone? (${(tones || []).join(" / ")})`, "neutral");
    if (!nextTone) return;
    if (!tones.includes(nextTone)) {
      toast.error(`Tone must be one of: ${tones.join(", ")}`);
      return;
    }
    try {
      await createChaserSmsTemplate({
        group: parseInt(nextGroup, 10),
        tone: nextTone,
        body: "New template — edit me",
      });
      toast.success("Template created");
      await load();
    } catch (e) {
      toast.error("Create failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const grouped = (() => {
    const g = {};
    (templates || []).forEach(t => {
      const k = t.group || "?";
      (g[k] = g[k] || []).push(t);
    });
    Object.values(g).forEach(arr => arr.sort((a, b) => (a.tone || "").localeCompare(b.tone || "")));
    return Object.entries(g).sort(([a], [b]) => Number(a) - Number(b));
  })();

  const toneBadge = (tone) => {
    const map = {
      friendly:   "bg-emerald-50 text-emerald-700 border-emerald-200",
      neutral:    "bg-slate-50 text-slate-700 border-slate-200",
      aggressive: "bg-red-50 text-red-700 border-red-200",
    };
    return `text-[10px] px-1.5 py-0.5 rounded border ${map[tone] || map.neutral}`;
  };

  return (
    <Card data-testid="chaser-sms-templates-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-violet-600" />
              Collections Chaser SMS Templates
            </CardTitle>
            <CardDescription>
              Pre-configured SMS templates for the case-page composer. Grouped by
              escalation stage (Chaser 1-4) with three tone variants each
              (a·friendly, b·neutral, c·aggressive). Placeholders are auto-filled
              at send time from the customer&apos;s case data.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={addNew}
              className="h-8 text-xs"
              data-testid="chaser-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAll}
              disabled={resetting}
              className="h-8 text-xs"
              data-testid="chaser-reset-btn"
            >
              {resetting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Restore defaults
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Placeholder reference chip strip */}
        <div className="rounded-md border bg-slate-50 p-3">
          <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
            Available placeholders — copy any of these into a template body:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => { navigator.clipboard?.writeText(p.key); toast.success(`Copied ${p.key}`); }}
                className="px-1.5 py-0.5 rounded border border-violet-200 bg-white hover:bg-violet-100 text-[10px] font-mono text-violet-700 transition-colors"
                title={`${p.label} — example: ${p.example} — click to copy`}
              >
                {p.key}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          grouped.map(([grp, tpls]) => (
            <div key={grp} className="space-y-2">
              <div className="flex items-center gap-2">
                {editingLabel === String(grp) ? (
                  <>
                    <Input
                      autoFocus
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveLabelEdit();
                        else if (e.key === "Escape") cancelLabelEdit();
                      }}
                      className="h-7 text-xs w-56"
                      placeholder={`Chaser ${grp}`}
                      data-testid={`chaser-group-label-input-${grp}`}
                    />
                    <Button
                      size="sm"
                      onClick={saveLabelEdit}
                      disabled={savingLabel}
                      className="h-7 text-[11px]"
                      data-testid={`chaser-group-label-save-${grp}`}
                    >
                      {savingLabel
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        : <Save className="w-3 h-3 mr-1" />}
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelLabelEdit}
                      className="h-7 text-[11px]"
                      data-testid={`chaser-group-label-cancel-${grp}`}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span
                      className="text-[13px] font-semibold text-slate-800"
                      data-testid={`chaser-group-label-${grp}`}
                    >
                      {groupLabels[String(grp)] || `Chaser ${grp}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => startLabelEdit(grp)}
                      className="text-[10px] text-violet-700 hover:underline underline-offset-2"
                      data-testid={`chaser-group-label-edit-${grp}`}
                    >
                      Rename
                    </button>
                    <span className="text-[10px] text-muted-foreground">
                      (group {grp} · shown in the case-page dropdown)
                    </span>
                  </>
                )}
              </div>
              {tpls.map(t => {
                const isEditing = editing[t.template_id] !== undefined;
                const draft = editing[t.template_id];
                return (
                  <div
                    key={t.template_id}
                    className={`rounded-md border p-3 space-y-2 ${t.active ? "bg-white" : "bg-slate-50 opacity-70"}`}
                    data-testid={`chaser-row-${t.template_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {isEditing ? (
                        <Input
                          value={draft.name}
                          onChange={(e) => setEditing(p => ({ ...p, [t.template_id]: { ...draft, name: e.target.value } }))}
                          className="h-7 text-xs w-40"
                          data-testid={`chaser-name-input-${t.template_id}`}
                        />
                      ) : (
                        <span className="font-semibold text-sm">{t.name}</span>
                      )}
                      <span className={toneBadge(t.tone)}>{t.tone}</span>
                      {!t.active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300">
                          disabled
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => cancelEdit(t.template_id)} className="h-7 text-[11px]" data-testid={`chaser-cancel-${t.template_id}`}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveEdit(t)} disabled={busyId === t.template_id} className="h-7 text-[11px]" data-testid={`chaser-save-${t.template_id}`}>
                              {busyId === t.template_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                              Save
                            </Button>
                          </>
                        ) : (
                          <>
                            <Switch
                              checked={t.active}
                              onCheckedChange={() => toggleActive(t)}
                              disabled={busyId === t.template_id}
                              data-testid={`chaser-toggle-${t.template_id}`}
                            />
                            <Button size="sm" variant="outline" onClick={() => startEdit(t)} className="h-7 text-[11px]" data-testid={`chaser-edit-${t.template_id}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(t)} disabled={busyId === t.template_id} className="h-7 text-[11px] text-rose-600 hover:text-rose-700" data-testid={`chaser-delete-${t.template_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {isEditing ? (
                      <>
                        <Textarea
                          value={draft.body}
                          onChange={(e) => setEditing(p => ({ ...p, [t.template_id]: { ...draft, body: e.target.value } }))}
                          rows={3}
                          className="text-xs"
                          data-testid={`chaser-body-input-${t.template_id}`}
                        />
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          {draft.body.length} chars · {Math.max(1, Math.ceil(draft.body.length / 160))} SMS segment{draft.body.length > 160 ? "s" : ""}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-slate-700 whitespace-pre-wrap font-mono bg-slate-50 rounded p-2">
                        {t.body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Chaser EMAIL Templates editor — mirrors the SMS editor exactly but adds
// a Subject field. Both live under the same "Comms Templates" tab so ops
// have one mental model for chaser copy across both channels.
// ══════════════════════════════════════════════════════════════════════════
function ChaserEmailTemplatesEditor() {
  const [templates, setTemplates]       = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [tones, setTones]               = useState([]);
  const [groupLabels, setGroupLabels]   = useState({});
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelDraft, setLabelDraft]     = useState("");
  const [savingLabel, setSavingLabel]   = useState(false);
  const [loading, setLoading]           = useState(true);
  const [busyId, setBusyId]             = useState(null);
  const [editing, setEditing]           = useState({});
  const [resetting, setResetting]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listChaserEmailTemplates({ onlyActive: false });
      setTemplates(r.templates || []);
      setPlaceholders(r.placeholders || []);
      setTones(r.tones || []);
      setGroupLabels(r.group_labels || {});
    } catch (e) {
      toast.error("Failed to load email templates: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startLabelEdit = (grp) => {
    setEditingLabel(String(grp));
    setLabelDraft(groupLabels[String(grp)] || `Chaser ${grp}`);
  };
  const cancelLabelEdit = () => { setEditingLabel(null); setLabelDraft(""); };
  const saveLabelEdit = async () => {
    if (!editingLabel) return;
    const clean = (labelDraft || "").trim();
    if (!clean) { toast.error("Label cannot be empty"); return; }
    setSavingLabel(true);
    try {
      const r = await updateChaserEmailGroupLabels({ [editingLabel]: clean });
      setGroupLabels(r.labels || {});
      toast.success(`Group ${editingLabel} renamed to "${clean}"`);
      cancelLabelEdit();
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSavingLabel(false);
    }
  };

  const startEdit = (t) => {
    setEditing(prev => ({ ...prev, [t.template_id]: { name: t.name, subject: t.subject, body: t.body } }));
  };
  const cancelEdit = (id) => {
    setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
  };
  const saveEdit = async (t) => {
    const draft = editing[t.template_id];
    if (!draft) return;
    if (!draft.subject || !draft.subject.trim()) { toast.error("Subject cannot be empty"); return; }
    if (!draft.body || !draft.body.trim()) { toast.error("Body cannot be empty"); return; }
    setBusyId(t.template_id);
    try {
      await updateChaserEmailTemplate(t.template_id, {
        name:    draft.name,
        subject: draft.subject,
        body:    draft.body,
      });
      toast.success(`Saved ${draft.name || t.name}`);
      cancelEdit(t.template_id);
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const toggleActive = async (t) => {
    setBusyId(t.template_id);
    try {
      await updateChaserEmailTemplate(t.template_id, { active: !t.active });
      await load();
    } catch (e) {
      toast.error("Toggle failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    setBusyId(t.template_id);
    try {
      await deleteChaserEmailTemplate(t.template_id);
      toast.success(`Deleted ${t.name}`);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusyId(null);
    }
  };
  const resetAll = async () => {
    if (!window.confirm("Reset all email templates to the seeded defaults? Any edits will be lost.")) return;
    setResetting(true);
    try {
      const r = await resetChaserEmailTemplates();
      toast.success(`Restored ${r.reseeded} default templates`);
      await load();
    } catch (e) {
      toast.error("Reset failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setResetting(false);
    }
  };
  const addNew = async () => {
    const nextGroup = window.prompt("Chaser group (1-4)?", "1");
    if (!nextGroup) return;
    const nextTone = window.prompt(`Tone? (${(tones || []).join(" / ")})`, "neutral");
    if (!nextTone) return;
    if (!tones.includes(nextTone)) {
      toast.error(`Tone must be one of: ${tones.join(", ")}`);
      return;
    }
    try {
      await createChaserEmailTemplate({
        group:   parseInt(nextGroup, 10),
        tone:    nextTone,
        subject: "New email template — edit me",
        body:    "Body — edit me",
      });
      toast.success("Template created");
      await load();
    } catch (e) {
      toast.error("Create failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const grouped = (() => {
    const g = {};
    (templates || []).forEach(t => {
      const k = t.group || "?";
      (g[k] = g[k] || []).push(t);
    });
    Object.values(g).forEach(arr => arr.sort((a, b) => (a.tone || "").localeCompare(b.tone || "")));
    return Object.entries(g).sort(([a], [b]) => Number(a) - Number(b));
  })();

  const toneBadge = (tone) => {
    const map = {
      friendly:   "bg-emerald-50 text-emerald-700 border-emerald-200",
      neutral:    "bg-slate-50 text-slate-700 border-slate-200",
      aggressive: "bg-red-50 text-red-700 border-red-200",
    };
    return `text-[10px] px-1.5 py-0.5 rounded border ${map[tone] || map.neutral}`;
  };

  return (
    <Card data-testid="chaser-email-templates-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Collections Chaser Email Templates
            </CardTitle>
            <CardDescription>
              Pre-configured email templates for the case-page composer. Same
              grouped structure as SMS (Chaser 1-4, tones a/b/c) but with a
              subject line and longer body. Placeholders hydrate at send time
              against the customer&apos;s live case data. Delivered as Arial 11pt.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={addNew}
              className="h-8 text-xs"
              data-testid="chaser-email-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAll}
              disabled={resetting}
              className="h-8 text-xs"
              data-testid="chaser-email-reset-btn"
            >
              {resetting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Restore defaults
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-slate-50 p-3">
          <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
            Available placeholders — copy into subject or body:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => { navigator.clipboard?.writeText(p.key); toast.success(`Copied ${p.key}`); }}
                className="px-1.5 py-0.5 rounded border border-blue-200 bg-white hover:bg-blue-100 text-[10px] font-mono text-blue-700 transition-colors"
                title={`${p.label} — example: ${p.example} — click to copy`}
              >
                {p.key}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          grouped.map(([grp, tpls]) => (
            <div key={grp} className="space-y-2">
              <div className="flex items-center gap-2">
                {editingLabel === String(grp) ? (
                  <>
                    <Input
                      autoFocus
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveLabelEdit();
                        else if (e.key === "Escape") cancelLabelEdit();
                      }}
                      className="h-7 text-xs w-56"
                      placeholder={`Chaser ${grp}`}
                      data-testid={`chaser-email-group-label-input-${grp}`}
                    />
                    <Button
                      size="sm"
                      onClick={saveLabelEdit}
                      disabled={savingLabel}
                      className="h-7 text-[11px]"
                      data-testid={`chaser-email-group-label-save-${grp}`}
                    >
                      {savingLabel ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelLabelEdit}
                      className="h-7 text-[11px]"
                      data-testid={`chaser-email-group-label-cancel-${grp}`}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span
                      className="text-[13px] font-semibold text-slate-800"
                      data-testid={`chaser-email-group-label-${grp}`}
                    >
                      {groupLabels[String(grp)] || `Chaser ${grp}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => startLabelEdit(grp)}
                      className="text-[10px] text-blue-700 hover:underline underline-offset-2"
                      data-testid={`chaser-email-group-label-edit-${grp}`}
                    >
                      Rename
                    </button>
                    <span className="text-[10px] text-muted-foreground">
                      (group {grp} · shown in the case-page dropdown)
                    </span>
                  </>
                )}
              </div>
              {tpls.map(t => {
                const isEditing = editing[t.template_id] !== undefined;
                const draft = editing[t.template_id];
                return (
                  <div
                    key={t.template_id}
                    className={`rounded-md border p-3 space-y-2 ${t.active ? "bg-white" : "bg-slate-50 opacity-70"}`}
                    data-testid={`chaser-email-row-${t.template_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {isEditing ? (
                        <Input
                          value={draft.name}
                          onChange={(e) => setEditing(p => ({ ...p, [t.template_id]: { ...draft, name: e.target.value } }))}
                          className="h-7 text-xs w-40"
                          data-testid={`chaser-email-name-input-${t.template_id}`}
                        />
                      ) : (
                        <span className="font-semibold text-sm">{t.name}</span>
                      )}
                      <span className={toneBadge(t.tone)}>{t.tone}</span>
                      {!t.active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300">
                          disabled
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => cancelEdit(t.template_id)} className="h-7 text-[11px]" data-testid={`chaser-email-cancel-${t.template_id}`}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveEdit(t)} disabled={busyId === t.template_id} className="h-7 text-[11px]" data-testid={`chaser-email-save-${t.template_id}`}>
                              {busyId === t.template_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                              Save
                            </Button>
                          </>
                        ) : (
                          <>
                            <Switch
                              checked={t.active}
                              onCheckedChange={() => toggleActive(t)}
                              disabled={busyId === t.template_id}
                              data-testid={`chaser-email-toggle-${t.template_id}`}
                            />
                            <Button size="sm" variant="outline" onClick={() => startEdit(t)} className="h-7 text-[11px]" data-testid={`chaser-email-edit-${t.template_id}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(t)} disabled={busyId === t.template_id} className="h-7 text-[11px] text-rose-600 hover:text-rose-700" data-testid={`chaser-email-delete-${t.template_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing ? (
                      <>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Subject</label>
                          <Input
                            value={draft.subject}
                            onChange={(e) => setEditing(p => ({ ...p, [t.template_id]: { ...draft, subject: e.target.value } }))}
                            className="h-7 text-xs"
                            data-testid={`chaser-email-subject-input-${t.template_id}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Body</label>
                          <Textarea
                            value={draft.body}
                            onChange={(e) => setEditing(p => ({ ...p, [t.template_id]: { ...draft, body: e.target.value } }))}
                            rows={8}
                            className="text-xs"
                            style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "11pt" }}
                            data-testid={`chaser-email-body-input-${t.template_id}`}
                          />
                        </div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          {(draft.body || "").length} chars
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[11px]">
                          <span className="text-muted-foreground uppercase tracking-wide text-[9px] mr-1.5">Subject:</span>
                          <span className="font-medium text-slate-800">{t.subject}</span>
                        </div>
                        <div
                          className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-2"
                          style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "11pt", lineHeight: 1.4 }}
                        >
                          {t.body}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}



// SMS Templates editor — supports multiple templates via the `endpoint` prop
function SmsTemplateEditor({
  endpoint = "/aus/sms-template",
  title = "GoCardless Mandate SMS Template",
  description = "Master template used when sending the GoCardless authorisation link via SMS.",
  testIdPrefix = "sms-template",
}) {
  const [template, setTemplate] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [placeholders, setPlaceholders] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const resp = await api.get(endpoint);
      setTemplate(resp.data.template);
      setDefaultTemplate(resp.data.default);
      setPlaceholders(resp.data.placeholders || []);
      setIsCustom(resp.data.is_custom);
    } catch (e) {
      toast.error("Failed to load SMS template");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.includes("{shortUrl}")) {
      toast.error("Template must include the {shortUrl} placeholder");
      return;
    }
    setSaving(true);
    try {
      await api.put(endpoint, { template });
      toast.success("SMS template saved");
      setIsCustom(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTemplate(defaultTemplate);
  };

  // Live preview with sample values
  const preview = template
    .replace(/\{firstName\}/g, "Dean")
    .replace(/\{customerName\}/g, "Dean Mitchell")
    .replace(/\{businessName\}/g, "Acme Pty Ltd")
    .replace(/\{scheme\}/g, "Direct Debit")
    .replace(/\{sfaNumber\}/g, "SFA-001")
    .replace(/\{shortUrl\}/g, "https://simplyfunded.au/r/Y7eMH");

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-emerald-600" />
          {title}
        </CardTitle>
        <CardDescription>
          {description}
          {isCustom && <span className="ml-2 inline-block bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full uppercase">Custom</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs font-medium">Template</Label>
          <Textarea
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={3}
            className="font-mono text-sm"
            data-testid={`${testIdPrefix}-textarea`}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {template.length} chars (1 segment ≤ 160 chars). Templates with multiple segments still send fine but cost more per send.
          </p>
        </div>

        <div>
          <Label className="text-xs font-medium">Available placeholders</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1 text-xs">
            {placeholders.map((p) => (
              <div key={p.key} className="flex items-start gap-2 py-1">
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(p.key)}
                  className="font-mono bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded text-emerald-700 cursor-pointer"
                  title="Click to copy"
                >
                  {p.key}
                </button>
                <span className="text-muted-foreground text-[11px] flex-1">{p.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <Label className="text-xs font-medium text-slate-700">Live preview (sample data)</Label>
          <p className="text-sm mt-1 italic text-slate-800 whitespace-pre-wrap" data-testid={`${testIdPrefix}-preview`}>
            {preview}
          </p>
          <p className="text-[11px] text-muted-foreground mt-2">
            {preview.length} chars
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="outline" size="sm" onClick={handleReset} data-testid={`${testIdPrefix}-reset-btn`}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Reset to default
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid={`${testIdPrefix}-save-btn`}>
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            <Save className="w-3 h-3 mr-1" />
            Save template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Company Witnesses — preloaded list of SF staff who can witness deeds.
// Feeds the witness dropdowns in the Onboarding Centre contract wizard.
function CompanyWitnessesSection({ user }) {
  const [witnesses, setWitnesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/company-witnesses")
      .then((res) => setWitnesses(res.data?.witnesses || []))
      .catch(() => toast.error("Could not load company witnesses"))
      .finally(() => setLoading(false));
  }, []);

  const updateRow = (idx, field, value) =>
    setWitnesses((prev) => prev.map((w, i) => (i === idx ? { ...w, [field]: value } : w)));

  const save = async () => {
    try {
      setSaving(true);
      const clean = witnesses.filter((w) => (w.name || "").trim());
      const res = await api.put(
        `/settings/company-witnesses?updated_by=${encodeURIComponent(user?.email || "admin")}`,
        { witnesses: clean }
      );
      setWitnesses(res.data?.witnesses || clean);
      toast.success("Company witnesses saved");
    } catch (err) {
      toast.error("Save failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="company-witnesses-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Company Witnesses
        </CardTitle>
        <CardDescription>
          Preloaded SimplyFunded staff who can witness deeds. They appear as a dropdown in the
          contract wizard wherever a witness is pre-assigned (FTLA / PG / Debenture).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {witnesses.length === 0 && (
              <p className="text-sm text-muted-foreground">No witnesses yet — add your first below.</p>
            )}
            {witnesses.map((w, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`witness-row-${idx}`}>
                <Input className="col-span-3 h-9" placeholder="Full name *" value={w.name || ""}
                  onChange={(e) => updateRow(idx, "name", e.target.value)} data-testid={`witness-name-${idx}`} />
                <Input className="col-span-3 h-9" type="email" placeholder="Email" value={w.email || ""}
                  onChange={(e) => updateRow(idx, "email", e.target.value)} data-testid={`witness-email-${idx}`} />
                <Input className="col-span-3 h-9" placeholder="Address" value={w.address || ""}
                  onChange={(e) => updateRow(idx, "address", e.target.value)} />
                <Input className="col-span-2 h-9" placeholder="Occupation" value={w.occupation || ""}
                  onChange={(e) => updateRow(idx, "occupation", e.target.value)} />
                <Button variant="ghost" size="sm" className="col-span-1 text-red-500 hover:text-red-700"
                  onClick={() => setWitnesses((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`witness-delete-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" size="sm"
                onClick={() => setWitnesses((prev) => [...prev, { name: "", email: "", address: "", occupation: "" }])}
                data-testid="add-witness-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Witness
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-witnesses-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save List
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Sales Chase Cadence — days until the NEXT offer chase is due (Sales Calendar)
function ChaseCadenceSection({ user }) {
  const FIELDS = [
    { key: "offer_chase_gap_1", label: "Offer → 1st chase due (days)", min: 0, max: 30 },
    { key: "offer_chase_gap_2", label: "1st chase → 2nd chase due (days)", min: 0, max: 30 },
    { key: "offer_chase_gap_3", label: "2nd chase → 3rd (final) chase due (days)", min: 0, max: 30 },
    { key: "chase_amber_days", label: "Traffic light — AMBER when not chased for (days)", min: 1, max: 30 },
    { key: "chase_red_days", label: "Traffic light — RED when not chased for (days)", min: 1, max: 60 },
    { key: "topup_threshold_pct", label: "LoanDisk top-up prospect threshold (% of loan repaid)", min: 1, max: 99 },
  ];
  const [vals, setVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPortalSettings()
      .then((s) => setVals({
        offer_chase_gap_1: s.offer_chase_gap_1 ?? 2,
        offer_chase_gap_2: s.offer_chase_gap_2 ?? 2,
        offer_chase_gap_3: s.offer_chase_gap_3 ?? 3,
        chase_amber_days: s.chase_amber_days ?? 1,
        chase_red_days: s.chase_red_days ?? 3,
        topup_threshold_pct: s.topup_threshold_pct ?? 50,
      }))
      .catch(() => toast.error("Could not load chase cadence"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const results = await Promise.allSettled(FIELDS.map((f) =>
        updateSetting(f.key, String(Math.max(f.min, Math.min(f.max, parseInt(vals[f.key], 10) || f.min))), user?.email || "admin")));
      const failed = results.filter(r => r.status === "rejected");
      if (failed.length) {
        toast.error(`Save failed for ${failed.length} setting(s): ` + (failed[0].reason?.response?.data?.detail || failed[0].reason?.message));
      } else {
        toast.success("Sales calendar settings saved — applied immediately");
      }
    } catch (err) {
      toast.error("Save failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="chase-cadence-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-orange-600" />Sales Calendar — Cadence, Traffic Lights & Top-ups</CardTitle>
        <CardDescription>
          Chase cadence sets when the NEXT offer chase becomes due. Traffic lights define what
          &ldquo;good&rdquo; looks like (green = chased within the amber threshold). The top-up threshold
          controls which open LoanDisk loans get pulled in as top-up prospects each morning.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-3 max-w-lg">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3">
                <Label className="text-sm">{f.label}</Label>
                <Input type="number" min={f.min} max={f.max} className="w-24 h-9"
                  value={vals[f.key] ?? ""} onChange={(e) => setVals(v => ({ ...v, [f.key]: e.target.value }))}
                  data-testid={`cadence-${f.key}`} />
              </div>
            ))}
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-cadence-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Settings
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Settings information architecture: country/scope -> sections -> settings
const SCOPES = [
  { key: "generic", label: "Generic", short: "Generic" },
  { key: "uk", label: "🇬🇧 United Kingdom", short: "UK" },
  { key: "aus", label: "🇦🇺 Australia", short: "AUS" },
];

const SETTINGS_SECTIONS = [
  { key: "general", scope: "generic", label: "General", icon: Settings, keywords: "monday refresh interval session timeout dev access password" },
  { key: "security", scope: "generic", label: "Security & Fraud", icon: Shield, keywords: "fraud detection quick signing witness threshold ip mismatch" },
  { key: "workflow", scope: "generic", label: "Workflow & Disbursement", icon: Clock, keywords: "net in hand consolidation expiry test payment amount random disbursement manager approval password notifications email" },
  { key: "api", scope: "generic", label: "API Credentials", icon: Key, keywords: "keys tokens docusign gocardless loandisk aircall monday clerk m365 companies house slack credentials mailbox traffic graph throttling microsoft" },
  { key: "data", scope: "generic", label: "Data Management", icon: Database, keywords: "monday sync schedule import export collections 2025 data" },
  { key: "slack-comms", scope: "generic", label: "Slack comms", icon: Hash, keywords: "slack communication flow thread events tags message templates funding channel test payment verifier dm" },
  { key: "xero", scope: "generic", label: "Xero", icon: Link2, keywords: "xero accounting invoices integration" },
  { key: "aircall-ai", scope: "generic", label: "Aircall AI", icon: Phone, keywords: "aircall ai call summary backfill timeline conversation intelligence" },
  { key: "bank-details", scope: "uk", label: "Receiving Bank Details", icon: Building2, keywords: "standing order bank account name number sort code simplyfunded receiving morley" },
  { key: "prefunding", scope: "uk", label: "Pre-Funding Call", icon: Phone, keywords: "questions call script pre fund funding" },
  { key: "witnesses", scope: "uk", label: "Company Witnesses", icon: Users, keywords: "witness docusign deed debenture pg preloaded staff contract wizard dropdown" },
  { key: "saleschase", scope: "uk", label: "Sales Chase Cadence", icon: Clock, keywords: "sales calendar offer chase due 1st 2nd 3rd cadence days diary traffic lights red amber green top up threshold percent loandisk prospects" },
  { key: "sms-templates", scope: "uk", label: "Comms Templates", icon: MessageSquare, keywords: "chaser sms email collections arrears templates" },
  { key: "notice-templates", scope: "uk", label: "Notice Templates", icon: FileText, keywords: "pdn dn default notice redemption settlement issue document templates expiry legal collections" },
  { key: "payout-letters", scope: "generic", label: "Payout Letters", icon: FileText, keywords: "payout settlement redemption letter discount approval threshold docupilot accounts email" },
  { key: "lenders", scope: "generic", label: "Third-Party Lenders", icon: Banknote, keywords: "consolidation lenders third party uk aus catalogue" },
  { key: "legal-diary", scope: "uk", label: "Legal Diary", icon: Scale, keywords: "traffic light thresholds scheduling caps legal diary" },
  { key: "complaints", scope: "uk", label: "Complaints", icon: MessageSquare, keywords: "complaints register deadlines templates" },
  { key: "aus-sms", scope: "aus", label: "SMS Templates", icon: MessageSquare, keywords: "gocardless mandate becs payto docusign signing sms australia templates" },
];

// Old tab keys from bookmarks / other pages keep working
const TAB_ALIASES = { settings: "general" };

export default function AdminSettings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [settings, setSettings] = useState(null);
  const [apiCredentials, setApiCredentials] = useState(null);
  const rawTab = searchParams.get("tab") || "general";
  const initialTab = TAB_ALIASES[rawTab] || rawTab;
  const [activeTab, setActiveTabState] = useState(
    SETTINGS_SECTIONS.some((s) => s.key === initialTab) ? initialTab : "general"
  );
  const [sectionQuery, setSectionQuery] = useState("");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const setActiveTab = (key) => {
    setActiveTabState(key);
    setSearchParams({ tab: key }, { replace: true });
    setSectionQuery("");
  };
  const activeScope = SETTINGS_SECTIONS.find((s) => s.key === activeTab)?.scope || "generic";
  const selectScope = (scopeKey) => {
    const first = SETTINGS_SECTIONS.find((s) => s.scope === scopeKey);
    if (first) setActiveTab(first.key);
  };
  const searchResults = sectionQuery.trim()
    ? SETTINGS_SECTIONS.filter((s) => `${s.label} ${s.keywords}`.toLowerCase().includes(sectionQuery.trim().toLowerCase()))
    : [];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsData, credentialsData] = await Promise.all([
        getPortalSettings(),
        getApiCredentials()
      ]);
      setSettings(settingsData);
      setApiCredentials(credentialsData);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      setSaving(key);
      await updateSetting(key, String(value), user?.email || "admin");
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success("Setting updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update setting");
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateApiCredentials = async (service, credentials) => {
    await updateApiCredentials(service, credentials);
    // Reload credentials to get updated masked values
    const updatedCredentials = await getApiCredentials();
    setApiCredentials(updatedCredentials);
  };

  const handleTestApiCredentials = async (service) => {
    return await testApiCredentials(service);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-800">Portal Settings</h1>
              <p className="text-sm text-muted-foreground">Configure system-wide settings and API integrations</p>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={sectionQuery}
                onChange={(e) => setSectionQuery(e.target.value)}
                placeholder="Search settings…"
                className="h-9 w-56 pl-8"
                data-testid="settings-search-input"
              />
              {sectionQuery.trim() && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border rounded-lg shadow-lg z-50 py-1 max-h-80 overflow-y-auto" data-testid="settings-search-results">
                  {searchResults.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No matching settings</p>
                  )}
                  {searchResults.map((s) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.key}
                        onClick={() => setActiveTab(s.key)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"
                        data-testid={`settings-search-result-${s.key}`}
                      >
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1">{s.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {SCOPES.find((sc) => sc.key === s.scope)?.short}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Country / scope switcher */}
        <div className="flex items-center gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit" data-testid="settings-scope-switcher">
          {SCOPES.map((sc) => (
            <button
              key={sc.key}
              onClick={() => selectScope(sc.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeScope === sc.key ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`settings-scope-${sc.key}`}
            >
              {sc.label}
            </button>
          ))}
        </div>

        {/* Sections within the selected scope */}
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="settings-section-nav">
          {SETTINGS_SECTIONS.filter((s) => s.scope === activeScope).map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setActiveTab(s.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  activeTab === s.key
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`settings-section-${s.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

          {activeTab === "general" && (
          <div className="space-y-6" data-testid="settings-content-general">
            <DevAccessCard />
            {/* Portal Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  Portal Settings
                </CardTitle>
                <CardDescription>General portal configuration</CardDescription>
              </CardHeader>
              <CardContent>
                <SettingRow
                  label="Monday.com Refresh Interval"
                  description="How often to auto-sync applications with Monday.com"
                  settingKey="monday_refresh_interval_mins"
                  suffix="minutes"
                  value={settings?.monday_refresh_interval_mins}
                  onSave={handleUpdateSetting}
                  saving={saving}
                />
                <SettingRow
                  label="Session Timeout"
                  description="Auto-logout after this period of inactivity"
                  settingKey="session_timeout_mins"
                  suffix="minutes"
                  value={settings?.session_timeout_mins}
                  onSave={handleUpdateSetting}
                  saving={saving}
                />
              </CardContent>
            </Card>
          </div>
          )}

          {activeTab === "security" && (
          <div className="space-y-6" data-testid="settings-content-security">
        {/* Security & Fraud Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              Security & Fraud Detection
            </CardTitle>
            <CardDescription>Configure fraud detection thresholds</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Quick Signing Threshold"
              description="Flag as potential fraud if signer completes faster than this"
              settingKey="quick_signing_threshold_seconds"
              suffix="seconds"
              value={settings?.quick_signing_threshold_seconds}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Quick Witness Threshold"
              description="Flag as potential fraud if witness signs faster than this"
              settingKey="quick_witness_threshold_seconds"
              suffix="seconds"
              value={settings?.quick_witness_threshold_seconds}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="IP Mismatch Detection"
              description="Check if signers are using different IP addresses"
              settingKey="ip_mismatch_enabled"
              type="boolean"
              value={settings?.ip_mismatch_enabled}
              onSave={handleUpdateSetting}
              saving={saving}
            />
          </CardContent>
        </Card>
          </div>
          )}

          {activeTab === "workflow" && (
          <div className="space-y-6" data-testid="settings-content-workflow">
        {/* Workflow Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Workflow Settings
            </CardTitle>
            <CardDescription>Configure workflow timeouts and defaults</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Net in Hand Expiry"
              description="How long before Net in Hand calculation expires and needs recalculation"
              settingKey="net_in_hand_expiry_mins"
              suffix="minutes"
              value={settings?.net_in_hand_expiry_mins}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Consolidation Expiry"
              description="How long before consolidation lender amounts need re-confirmation"
              settingKey="consolidation_expiry_hours"
              suffix="hours"
              value={settings?.consolidation_expiry_hours}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            
            {/* Test Payment Settings */}
            <div className="pt-3 mt-3 border-t">
              <p className="font-medium text-sm mb-3">Test Payment Amount</p>
              <SettingRow
                label="Use Random Amount"
                description="Generate a random amount for each test payment (more secure)"
                settingKey="test_payment_random"
                type="boolean"
                value={settings?.test_payment_random}
                onSave={handleUpdateSetting}
                saving={saving}
              />
              {settings?.test_payment_random ? (
                <>
                  <SettingRow
                    label="Minimum Amount"
                    description="Minimum random test payment amount"
                    settingKey="test_payment_min_amount"
                    type="number"
                    suffix="GBP"
                    value={settings?.test_payment_min_amount}
                    onSave={handleUpdateSetting}
                    saving={saving}
                  />
                  <SettingRow
                    label="Maximum Amount"
                    description="Maximum random test payment amount"
                    settingKey="test_payment_max_amount"
                    type="number"
                    suffix="GBP"
                    value={settings?.test_payment_max_amount}
                    onSave={handleUpdateSetting}
                    saving={saving}
                  />
                </>
              ) : (
                <SettingRow
                  label="Fixed Amount"
                  description="Fixed test payment amount when not using random"
                  settingKey="test_payment_default_amount"
                  type="number"
                  suffix="GBP"
                  value={settings?.test_payment_default_amount}
                  onSave={handleUpdateSetting}
                  saving={saving}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Disbursement Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Disbursement Settings
            </CardTitle>
            <CardDescription>Configure disbursement approval requirements</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Require Manager Approval"
              description="All disbursements must be approved by a manager"
              settingKey="require_manager_approval"
              type="boolean"
              value={settings?.require_manager_approval}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Require Password Re-entry"
              description="User must re-enter password before executing disbursement"
              settingKey="require_password_reentry"
              type="boolean"
              value={settings?.require_password_reentry}
              onSave={handleUpdateSetting}
              saving={saving}
            />
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-purple-600" />
              Notification Settings
            </CardTitle>
            <CardDescription>Configure email notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Email on Approval Request"
              description="Send email to managers when approval is requested"
              settingKey="send_email_on_approval_request"
              type="boolean"
              value={settings?.send_email_on_approval_request}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Email on Disbursement"
              description="Send confirmation email when disbursement is completed"
              settingKey="send_email_on_disbursement"
              type="boolean"
              value={settings?.send_email_on_disbursement}
              onSave={handleUpdateSetting}
              saving={saving}
            />
          </CardContent>
        </Card>
          </div>
          )}

        {/* SimplyFunded Receiving Bank Details */}
        {activeTab === "bank-details" && (
        <div className="space-y-6" data-testid="settings-content-bank-details">
        <Card className={!settings?.sf_bank_name || !settings?.sf_account_number || !settings?.sf_sort_code ? "border-amber-300 bg-amber-50/50" : ""}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              SimplyFunded Receiving Bank Details
            </CardTitle>
            <CardDescription>
              Bank details used in Standing Order emails sent to customers
            </CardDescription>
            {(!settings?.sf_bank_name || !settings?.sf_account_number || !settings?.sf_sort_code) && (
              <div className="mt-2 flex items-center gap-2 text-amber-700 bg-amber-100 px-3 py-2 rounded-md text-sm">
                <XCircle className="w-4 h-4" />
                <span>Warning: Bank details not fully configured. Standing Order emails will show &quot;Not Configured&quot;.</span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <SettingRow
              label="Bank Name"
              description="Name of the bank (e.g., Barclays)"
              settingKey="sf_bank_name"
              type="text"
              value={settings?.sf_bank_name}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Account Name"
              description="Name on the account (e.g., Morley Capital Ltd)"
              settingKey="sf_account_name"
              type="text"
              value={settings?.sf_account_name}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Account Number"
              description="8-digit bank account number"
              settingKey="sf_account_number"
              type="text"
              value={settings?.sf_account_number}
              onSave={handleUpdateSetting}
              saving={saving}
            />
            <SettingRow
              label="Sort Code"
              description="6-digit sort code (e.g., 20-00-00)"
              settingKey="sf_sort_code"
              type="text"
              value={settings?.sf_sort_code}
              onSave={handleUpdateSetting}
              saving={saving}
            />
          </CardContent>
        </Card>

        {/* Last Updated Info */}
        {settings?.updated_at && (
          <p className="text-xs text-muted-foreground text-center">
            Last updated: {new Date(settings.updated_at).toLocaleString('en-GB')} by {settings.updated_by}
          </p>
        )}
        </div>
        )}

          {activeTab === "api" && (
          <div className="space-y-6" data-testid="settings-content-api">
            <MailboxTrafficCard />
            <PartnerApiKeysCard />
            <LoanDiskWebhooksCard />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {apiCredentials && Object.entries(apiCredentials).map(([serviceId, service]) => (
                <ApiCredentialCard
                  key={serviceId}
                  serviceId={serviceId}
                  service={service}
                  onUpdate={handleUpdateApiCredentials}
                  onTest={handleTestApiCredentials}
                />
              ))}
            </div>
            
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> API credentials are stored securely in the database and loaded on server startup. 
                  Changes take effect immediately and persist across deployments.
                </p>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Pre-Funding Call Questions */}
          {activeTab === "prefunding" && (
          <div className="space-y-6" data-testid="settings-content-prefunding">
            <PreFundingQuestionsManager />
          </div>
          )}

          {/* Company Witnesses (UK) */}
          {activeTab === "witnesses" && (
          <div className="space-y-6" data-testid="settings-content-witnesses">
            <CompanyWitnessesSection user={user} />
          </div>
          )}

          {/* Sales Chase Cadence (UK) */}
          {activeTab === "saleschase" && (
          <div className="space-y-6" data-testid="settings-content-saleschase">
            <ChaseCadenceSection user={user} />
          </div>
          )}

          {/* Data Management */}
          {activeTab === "data" && (
          <div className="space-y-6" data-testid="settings-content-data">
            <MondaySyncSection />
            <DataManagementSection />
          </div>
          )}

          {/* UK Comms Templates */}
          {activeTab === "sms-templates" && (
          <div className="space-y-6" data-testid="settings-content-sms-templates">
            <ChaserSmsTemplatesEditor />
            <ChaserEmailTemplatesEditor />
          </div>
          )}

          {/* AUS SMS Templates */}
          {activeTab === "aus-sms" && (
          <div className="space-y-6" data-testid="settings-content-aus-sms">
            <SmsTemplateEditor
              endpoint="/aus/sms-template"
              title="GoCardless Mandate SMS Template"
              description="Sent in Step 6 (Customer Onboarding) when issuing the BECS/PayTo mandate. Operators can still tweak it per-customer before sending."
              testIdPrefix="gc-sms-template"
            />
            <SmsTemplateEditor
              endpoint="/aus/docusign-sms-template"
              title="DocuSign Signing SMS Template"
              description="Sent in Step 5 (Documents & E-Signing) when the loan agreement envelope is created. The short link generates a fresh DocuSign URL on each click so the SMS never expires."
              testIdPrefix="docusign-sms-template"
            />
          </div>
          )}

          {/* Third-Party Lenders */}
          {activeTab === "lenders" && (
          <div className="space-y-6" data-testid="settings-content-lenders">
            <ThirdPartyLendersSettings />
          </div>
          )}

          {/* Legal Diary — traffic-light thresholds + scheduling caps */}
          {activeTab === "legal-diary" && (
          <div className="space-y-6" data-testid="settings-content-legal-diary">
            <LegalDiarySettings />
            <AccountStatusSettings />
          </div>
          )}

          {activeTab === "complaints" && (
          <div className="space-y-6" data-testid="settings-content-complaints">
            <ComplaintsSettings />
          </div>
          )}

          {/* Notice / document-issue templates (PDN / DN / …) */}
          {activeTab === "payout-letters" && <PayoutLetterSettings />}
          {activeTab === "notice-templates" && (
          <div className="space-y-6" data-testid="settings-content-notice-templates">
            <NoticeTemplatesSettings />
          </div>
          )}

          {/* Xero Integration */}
          {activeTab === "xero" && (
          <div className="space-y-6" data-testid="settings-content-xero">
            <XeroSettings />
          </div>
          )}

          {/* Aircall AI summaries */}
          {activeTab === "aircall-ai" && (
          <div className="space-y-6" data-testid="settings-content-aircall-ai">
            <AircallAiSettings />
          </div>
          )}

          {/* Slack communication flow */}
          {activeTab === "slack-comms" && (
          <div className="space-y-6" data-testid="settings-content-slack-comms">
            <SlackCommsSettings />
          </div>
          )}
      </div>
    </div>
  );
}
